import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  BarChart3, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, 
  Calendar, Check, Minus, Pickaxe, Factory, Gauge, MapPin, Filter
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList
} from 'recharts';
import PageHeader from '../../components/PageHeader';

/**
 * OperationsMultiPeriodComparative - Multi-period comparison page for Operations
 * Allows board members to compare operational performance across multiple periods
 */

const colors = {
  primary: '#0A192F',
  gold: '#C5A059',
  positive: '#059669',
  negative: '#DC2626',
  neutral: '#64748B',
  chart: ['#0A192F', '#C5A059', '#3B82F6', '#10B981']
};

const OperationsMultiPeriodComparative = ({ year, language, flowMap }) => {
  const [selectedPeriods, setSelectedPeriods] = useState(['2026-Q1', '2026-Q1-Budget', '2025-Q1']);
  const [selectedIndicator, setSelectedIndicator] = useState('caratsProduced');
  const [selectedMine, setSelectedMine] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  
  // Available mines and regions for filtering
  const availableMines = [
    { id: 'all', label: { pt: 'Todas as Minas', en: 'All Mines' } },
    { id: 'mina-3', label: { pt: 'Mina 3', en: 'Mine 3' } },
    { id: 'mina-4', label: { pt: 'Mina 4', en: 'Mine 4' } },
    { id: 'calonda', label: { pt: 'Calonda', en: 'Calonda' } },
    { id: 'lulo', label: { pt: 'Lulo', en: 'Lulo' } },
  ];
  
  const availableRegions = [
    { id: 'all', label: { pt: 'Todas as Regiões', en: 'All Regions' } },
    { id: 'norte', label: { pt: 'Zona Norte', en: 'North Zone' } },
    { id: 'sul', label: { pt: 'Zona Sul', en: 'South Zone' } },
    { id: 'central', label: { pt: 'Zona Central', en: 'Central Zone' } },
  ];
  
  // Period structure for table display
  const annualPeriods = [
    { year: 2026, real: null, budget: '2026-Budget' },
    { year: 2025, real: '2025-FY', budget: '2025-Budget' },
    { year: 2024, real: '2024-FY', budget: null },
  ];

  const quarterlyPeriods = [
    { quarter: '4T26', real: null, budget: '2026-Q4-Budget' },
    { quarter: '3T26', real: null, budget: '2026-Q3-Budget' },
    { quarter: '2T26', real: null, budget: '2026-Q2-Budget' },
    { quarter: '1T26', real: '2026-Q1', budget: '2026-Q1-Budget' },
    { quarter: '4T25', real: '2025-Q4', budget: '2025-Q4-Budget' },
    { quarter: '3T25', real: '2025-Q3', budget: '2025-Q3-Budget' },
    { quarter: '2T25', real: '2025-Q2', budget: '2025-Q2-Budget' },
    { quarter: '1T25', real: '2025-Q1', budget: '2025-Q1-Budget' },
  ];

  // All available periods with metadata
  const availablePeriods = [
    { id: '2024-FY', label: { pt: '2024', en: '2024' }, year: 2024, category: 'annual', isReal: true },
    { id: '2025-FY', label: { pt: '2025', en: '2025' }, year: 2025, category: 'annual', isReal: true },
    { id: '2025-Budget', label: { pt: 'Orç. 2025', en: 'Budget 2025' }, year: 2025, category: 'annual', isReal: false },
    { id: '2026-Budget', label: { pt: 'Orç. 2026', en: 'Budget 2026' }, year: 2026, category: 'annual', isReal: false },
    { id: '2025-Q1', label: { pt: '1T25', en: 'Q1 25' }, year: 2025, quarter: 1, category: 'quarter', isReal: true },
    { id: '2025-Q2', label: { pt: '2T25', en: 'Q2 25' }, year: 2025, quarter: 2, category: 'quarter', isReal: true },
    { id: '2025-Q3', label: { pt: '3T25', en: 'Q3 25' }, year: 2025, quarter: 3, category: 'quarter', isReal: true },
    { id: '2025-Q4', label: { pt: '4T25', en: 'Q4 25' }, year: 2025, quarter: 4, category: 'quarter', isReal: true },
    { id: '2025-Q1-Budget', label: { pt: 'Orç. 1T25', en: 'Budget Q1 25' }, year: 2025, quarter: 1, category: 'quarter', isReal: false },
    { id: '2025-Q2-Budget', label: { pt: 'Orç. 2T25', en: 'Budget Q2 25' }, year: 2025, quarter: 2, category: 'quarter', isReal: false },
    { id: '2025-Q3-Budget', label: { pt: 'Orç. 3T25', en: 'Budget Q3 25' }, year: 2025, quarter: 3, category: 'quarter', isReal: false },
    { id: '2025-Q4-Budget', label: { pt: 'Orç. 4T25', en: 'Budget Q4 25' }, year: 2025, quarter: 4, category: 'quarter', isReal: false },
    { id: '2026-Q1', label: { pt: '1T26', en: 'Q1 26' }, year: 2026, quarter: 1, category: 'quarter', isReal: true },
    { id: '2026-Q1-Budget', label: { pt: 'Orç. 1T26', en: 'Budget Q1 26' }, year: 2026, quarter: 1, category: 'quarter', isReal: false },
    { id: '2026-Q2-Budget', label: { pt: 'Orç. 2T26', en: 'Budget Q2 26' }, year: 2026, quarter: 2, category: 'quarter', isReal: false },
    { id: '2026-Q3-Budget', label: { pt: 'Orç. 3T26', en: 'Budget Q3 26' }, year: 2026, quarter: 3, category: 'quarter', isReal: false },
    { id: '2026-Q4-Budget', label: { pt: 'Orç. 4T26', en: 'Budget Q4 26' }, year: 2026, quarter: 4, category: 'quarter', isReal: false },
  ];

  // Get the category of currently selected periods
  const getSelectedCategory = () => {
    if (selectedPeriods.length === 0) return null;
    const firstPeriod = availablePeriods.find(p => p.id === selectedPeriods[0]);
    return firstPeriod?.category || null;
  };

  const canSelectPeriod = (periodId) => {
    if (!periodId) return false;
    const period = availablePeriods.find(p => p.id === periodId);
    if (!period) return false;
    const selectedCategory = getSelectedCategory();
    if (!selectedCategory || selectedPeriods.length === 0) return true;
    return period.category === selectedCategory;
  };

  const togglePeriod = (periodId) => {
    if (!periodId) return;
    if (selectedPeriods.includes(periodId)) {
      setSelectedPeriods(selectedPeriods.filter(p => p !== periodId));
    } else {
      if (selectedPeriods.length < 4 && canSelectPeriod(periodId)) {
        setSelectedPeriods([...selectedPeriods, periodId]);
      }
    }
  };

  const getSortedSelectedPeriods = () => {
    return [...selectedPeriods].sort((a, b) => {
      const periodA = availablePeriods.find(p => p.id === a);
      const periodB = availablePeriods.find(p => p.id === b);
      if (!periodA || !periodB) return 0;
      if (periodB.year !== periodA.year) return periodB.year - periodA.year;
      if (periodA.quarter && periodB.quarter && periodB.quarter !== periodA.quarter) {
        return periodB.quarter - periodA.quarter;
      }
      if (periodA.isReal && !periodB.isReal) return -1;
      if (!periodA.isReal && periodB.isReal) return 1;
      return 0;
    });
  };

  const sortedSelectedPeriods = getSortedSelectedPeriods();
  const selectedCategory = getSelectedCategory();

  // Available indicators for selection - Operational KPIs
  const availableIndicators = [
    { key: 'caratsProduced', label: { pt: 'Quilates Recuperados', en: 'Carats Recovered' }, unit: 'cts', color: '#0A192F' },
    { key: 'oreProcessed', label: { pt: 'Minério Processado', en: 'Ore Processed' }, unit: 't', color: '#C5A059' },
    { key: 'recoveryRate', label: { pt: 'Taxa de Recuperação', en: 'Recovery Rate' }, unit: 'cpht', color: '#3B82F6' },
    { key: 'plantAvailability', label: { pt: 'Disponibilidade Planta', en: 'Plant Availability' }, unit: '%', color: '#10B981' },
    { key: 'mineAdvance', label: { pt: 'Avanço de Mina', en: 'Mine Advance' }, unit: 'm', color: '#8B5CF6' },
    { key: 'wasteRemoved', label: { pt: 'Estéril Removido', en: 'Waste Removed' }, unit: 't', color: '#F59E0B' },
    { key: 'stripRatio', label: { pt: 'Strip Ratio', en: 'Strip Ratio' }, unit: ':1', color: '#EF4444', inverse: true },
    { key: 'oee', label: { pt: 'OEE', en: 'OEE' }, unit: '%', color: '#06B6D4' },
  ];

  // Mock data for operational comparison
  const periodData = {
    // Annual
    '2024-FY': { caratsProduced: 168200, oreProcessed: 4250000, recoveryRate: 15.65, plantAvailability: 93.5, mineAdvance: 22400, wasteRemoved: 8500000, stripRatio: 2.0, oee: 79.5 },
    '2025-FY': { caratsProduced: 182500, oreProcessed: 4580000, recoveryRate: 15.94, plantAvailability: 94.2, mineAdvance: 23800, wasteRemoved: 8750000, stripRatio: 1.91, oee: 81.2 },
    '2025-Budget': { caratsProduced: 190000, oreProcessed: 4800000, recoveryRate: 16.07, plantAvailability: 95.0, mineAdvance: 24500, wasteRemoved: 8800000, stripRatio: 1.83, oee: 82.0 },
    '2026-Budget': { caratsProduced: 195000, oreProcessed: 4950000, recoveryRate: 16.2, plantAvailability: 95.5, mineAdvance: 25200, wasteRemoved: 9100000, stripRatio: 1.84, oee: 83.0 },
    // Quarterly 2025 Real
    '2025-Q1': { caratsProduced: 43500, oreProcessed: 1100000, recoveryRate: 15.82, plantAvailability: 93.8, mineAdvance: 5700, wasteRemoved: 2150000, stripRatio: 1.95, oee: 80.2 },
    '2025-Q2': { caratsProduced: 44800, oreProcessed: 1130000, recoveryRate: 15.87, plantAvailability: 94.0, mineAdvance: 5850, wasteRemoved: 2180000, stripRatio: 1.93, oee: 80.8 },
    '2025-Q3': { caratsProduced: 46200, oreProcessed: 1165000, recoveryRate: 15.91, plantAvailability: 94.3, mineAdvance: 6000, wasteRemoved: 2200000, stripRatio: 1.89, oee: 81.5 },
    '2025-Q4': { caratsProduced: 48000, oreProcessed: 1200000, recoveryRate: 16.00, plantAvailability: 94.8, mineAdvance: 6250, wasteRemoved: 2220000, stripRatio: 1.85, oee: 82.3 },
    // Quarterly 2025 Budget
    '2025-Q1-Budget': { caratsProduced: 45000, oreProcessed: 1140000, recoveryRate: 16.00, plantAvailability: 95.0, mineAdvance: 5900, wasteRemoved: 2100000, stripRatio: 1.84, oee: 82.0 },
    '2025-Q2-Budget': { caratsProduced: 46500, oreProcessed: 1175000, recoveryRate: 16.03, plantAvailability: 95.0, mineAdvance: 6050, wasteRemoved: 2150000, stripRatio: 1.83, oee: 82.0 },
    '2025-Q3-Budget': { caratsProduced: 48000, oreProcessed: 1210000, recoveryRate: 16.07, plantAvailability: 95.0, mineAdvance: 6200, wasteRemoved: 2200000, stripRatio: 1.82, oee: 82.0 },
    '2025-Q4-Budget': { caratsProduced: 50500, oreProcessed: 1275000, recoveryRate: 16.10, plantAvailability: 95.0, mineAdvance: 6350, wasteRemoved: 2350000, stripRatio: 1.84, oee: 82.0 },
    // Quarterly 2026 Real
    '2026-Q1': { caratsProduced: 42500, oreProcessed: 1080000, recoveryRate: 15.74, plantAvailability: 92.3, mineAdvance: 5200, wasteRemoved: 2100000, stripRatio: 1.94, oee: 78.5 },
    // Quarterly 2026 Budget
    '2026-Q1-Budget': { caratsProduced: 45000, oreProcessed: 1150000, recoveryRate: 16.07, plantAvailability: 95.0, mineAdvance: 5600, wasteRemoved: 2200000, stripRatio: 1.91, oee: 82.0 },
    '2026-Q2-Budget': { caratsProduced: 46500, oreProcessed: 1187000, recoveryRate: 16.12, plantAvailability: 95.2, mineAdvance: 5750, wasteRemoved: 2250000, stripRatio: 1.90, oee: 82.5 },
    '2026-Q3-Budget': { caratsProduced: 48000, oreProcessed: 1225000, recoveryRate: 16.15, plantAvailability: 95.4, mineAdvance: 5900, wasteRemoved: 2300000, stripRatio: 1.88, oee: 83.0 },
    '2026-Q4-Budget': { caratsProduced: 50500, oreProcessed: 1288000, recoveryRate: 16.20, plantAvailability: 95.8, mineAdvance: 6200, wasteRemoved: 2350000, stripRatio: 1.83, oee: 83.5 },
  };

  // KPI definitions for Operations
  const kpiDefinitions = [
    { key: 'caratsProduced', label: { pt: 'Quilates Recuperados', en: 'Carats Recovered' }, unit: 'cts', format: (v) => v.toLocaleString() },
    { key: 'oreProcessed', label: { pt: 'Minério Processado', en: 'Ore Processed' }, unit: 't', format: (v) => v.toLocaleString() },
    { key: 'recoveryRate', label: { pt: 'Taxa de Recuperação', en: 'Recovery Rate' }, unit: 'cpht', format: (v) => v.toFixed(2) },
    { key: 'plantAvailability', label: { pt: 'Disponibilidade Planta', en: 'Plant Availability' }, unit: '%', format: (v) => v.toFixed(1) },
    { key: 'mineAdvance', label: { pt: 'Avanço de Mina', en: 'Mine Advance' }, unit: 'm', format: (v) => v.toLocaleString() },
    { key: 'wasteRemoved', label: { pt: 'Estéril Removido', en: 'Waste Removed' }, unit: 't', format: (v) => v.toLocaleString() },
    { key: 'stripRatio', label: { pt: 'Strip Ratio', en: 'Strip Ratio' }, unit: ':1', format: (v) => v.toFixed(2), inverse: true },
    { key: 'oee', label: { pt: 'OEE', en: 'OEE' }, unit: '%', format: (v) => v.toFixed(1) },
  ];

  // Get selected indicator details
  const currentIndicator = availableIndicators.find(i => i.key === selectedIndicator);

  // Calculate variance between first sorted period and others
  const getVariance = (kpi, periodId) => {
    if (periodId === sortedSelectedPeriods[0]) return null;
    const base = periodData[sortedSelectedPeriods[0]][kpi.key];
    const current = periodData[periodId][kpi.key];
    const variance = ((current - base) / base * 100);
    return variance;
  };

  // Prepare chart data for selected indicator (use sorted periods)
  const chartData = sortedSelectedPeriods.map(periodId => {
    const period = availablePeriods.find(p => p.id === periodId);
    const data = periodData[periodId];
    return {
      period: period.label[language],
      value: data[selectedIndicator],
      periodId: periodId
    };
  }).reverse();

  return (
    <div className="space-y-8" data-testid="operations-multi-period-comparative">
      {/* Page Header */}
      <PageHeader
        title={language === 'pt' ? 'Comparativo Multi-Período' : 'Multi-Period Comparison'}
        period=""
        subtitle={language === 'pt' 
          ? 'Análise comparativa de performance operacional entre períodos selecionados'
          : 'Comparative analysis of operational performance across selected periods'}
        language={language}
        flowMap={flowMap}
      />

      {/* Filters Row - Mine and Region */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#112240] rounded-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <Filter className="w-4 h-4 text-gold-400" />
          <h3 className="text-white font-medium text-xs uppercase tracking-wider">
            {language === 'pt' ? 'Filtros de Segmentação' : 'Segmentation Filters'}
          </h3>
          {(selectedMine !== 'all' || selectedRegion !== 'all') && (
            <button 
              onClick={() => { setSelectedMine('all'); setSelectedRegion('all'); }}
              className="text-[10px] text-slate-400 hover:text-white ml-auto flex items-center gap-1 transition-colors"
            >
              <span>×</span>
              {language === 'pt' ? 'Limpar filtros' : 'Clear filters'}
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {/* Mine Filter */}
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Pickaxe className="w-3 h-3" />
              {language === 'pt' ? 'Filtrar por Mina' : 'Filter by Mine'}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {availableMines.map(mine => (
                <button
                  key={mine.id}
                  onClick={() => setSelectedMine(mine.id)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${
                    selectedMine === mine.id
                      ? 'bg-gold-500 text-primary'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20'
                  }`}
                >
                  {mine.label[language]}
                </button>
              ))}
            </div>
          </div>
          
          {/* Region Filter */}
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" />
              {language === 'pt' ? 'Filtrar por Região' : 'Filter by Region'}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {availableRegions.map(region => (
                <button
                  key={region.id}
                  onClick={() => setSelectedRegion(region.id)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${
                    selectedRegion === region.id
                      ? 'bg-blue-500 text-white'
                      : 'bg-white/10 text-slate-300 hover:bg-white/20'
                  }`}
                >
                  {region.label[language]}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Active Filters Summary */}
        {(selectedMine !== 'all' || selectedRegion !== 'all') && (
          <div className="mt-3 pt-3 border-t border-white/10">
            <p className="text-[10px] text-slate-400">
              {language === 'pt' ? 'A mostrar dados para: ' : 'Showing data for: '}
              <span className="text-white font-medium">
                {selectedMine !== 'all' && availableMines.find(m => m.id === selectedMine)?.label[language]}
                {selectedMine !== 'all' && selectedRegion !== 'all' && ' • '}
                {selectedRegion !== 'all' && availableRegions.find(r => r.id === selectedRegion)?.label[language]}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Combined Selector - Periods (Left) and Indicator (Right) */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="grid grid-cols-2 gap-6">
          {/* Left: Period Selector */}
          <div className="border-r border-slate-200 pr-6">
            <h2 className="font-serif text-sm font-semibold text-primary mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" style={{ color: colors.gold }} />
              {language === 'pt' ? 'Selecionar Períodos (máx. 4)' : 'Select Periods (max. 4)'}
            </h2>
            
            {/* Note about comparable periods */}
            <p className="text-[10px] text-slate-500 mb-2 bg-slate-50 p-2 rounded border border-slate-200">
              {language === 'pt' 
                ? '⚠️ Só é possível comparar períodos do mesmo tipo: trimestre com trimestre ou ano com ano.'
                : '⚠️ Only comparable periods can be selected: quarter with quarter or year with year.'}
            </p>
            
            {/* Selection mode indicator */}
            {selectedPeriods.length > 0 && (
              <p className="text-[10px] text-slate-500 mb-2 italic">
                {language === 'pt' 
                  ? `Modo: ${selectedCategory === 'quarter' ? 'Trimestral' : 'Anual'} — clique para desselecionar`
                  : `Mode: ${selectedCategory === 'quarter' ? 'Quarterly' : 'Annual'} — click to deselect`}
              </p>
            )}
            
            {/* Period Selection Table */}
            <div className="grid grid-cols-2 gap-4">
              {/* Annual Column */}
              <div className={`border rounded-lg overflow-hidden ${selectedCategory === 'quarter' ? 'opacity-40' : ''}`}>
                <div className="bg-emerald-600 text-white text-[10px] font-bold text-center py-1.5">
                  {language === 'pt' ? 'ANO' : 'YEAR'}
                </div>
                <div className="grid grid-cols-2 text-[9px] font-semibold text-center border-b bg-slate-50">
                  <div className="py-1 border-r text-emerald-700">{language === 'pt' ? 'Real' : 'Actual'}</div>
                  <div className="py-1 text-amber-700">{language === 'pt' ? 'Orçamento' : 'Budget'}</div>
                </div>
                {annualPeriods.map((row, idx) => (
                  <div key={row.year} className={`grid grid-cols-2 text-[10px] ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    <div className="py-1.5 px-1 border-r flex justify-center">
                      {row.real ? (
                        <button
                          onClick={() => togglePeriod(row.real)}
                          disabled={selectedCategory === 'quarter'}
                          className={`px-2 py-0.5 rounded transition-all ${
                            selectedPeriods.includes(row.real)
                              ? 'bg-emerald-500 text-white font-semibold'
                              : 'hover:bg-emerald-100 text-slate-700'
                          } ${selectedCategory === 'quarter' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {row.year}
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </div>
                    <div className="py-1.5 px-1 flex justify-center">
                      {row.budget ? (
                        <button
                          onClick={() => togglePeriod(row.budget)}
                          disabled={selectedCategory === 'quarter'}
                          className={`px-2 py-0.5 rounded transition-all ${
                            selectedPeriods.includes(row.budget)
                              ? 'bg-amber-500 text-white font-semibold'
                              : 'hover:bg-amber-100 text-slate-700'
                          } ${selectedCategory === 'quarter' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {row.year}
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quarterly Column */}
              <div className={`border rounded-lg overflow-hidden ${selectedCategory === 'annual' ? 'opacity-40' : ''}`}>
                <div className="bg-blue-600 text-white text-[10px] font-bold text-center py-1.5">
                  {language === 'pt' ? 'TRIMESTRE' : 'QUARTER'}
                </div>
                <div className="grid grid-cols-2 text-[9px] font-semibold text-center border-b bg-slate-50">
                  <div className="py-1 border-r text-blue-700">{language === 'pt' ? 'Real' : 'Actual'}</div>
                  <div className="py-1 text-cyan-700">{language === 'pt' ? 'Orçamento' : 'Budget'}</div>
                </div>
                <div className="max-h-36 overflow-y-auto">
                  {quarterlyPeriods.map((row, idx) => (
                    <div key={row.quarter} className={`grid grid-cols-2 text-[10px] ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                      <div className="py-1 px-1 border-r flex justify-center">
                        {row.real ? (
                          <button
                            onClick={() => togglePeriod(row.real)}
                            disabled={selectedCategory === 'annual'}
                            className={`px-2 py-0.5 rounded transition-all ${
                              selectedPeriods.includes(row.real)
                                ? 'bg-blue-500 text-white font-semibold'
                                : 'hover:bg-blue-100 text-slate-700'
                            } ${selectedCategory === 'annual' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {row.quarter}
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </div>
                      <div className="py-1 px-1 flex justify-center">
                        {row.budget ? (
                          <button
                            onClick={() => togglePeriod(row.budget)}
                            disabled={selectedCategory === 'annual'}
                            className={`px-2 py-0.5 rounded transition-all ${
                              selectedPeriods.includes(row.budget)
                                ? 'bg-cyan-500 text-white font-semibold'
                                : 'hover:bg-cyan-100 text-slate-700'
                            } ${selectedCategory === 'annual' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {row.quarter}
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Indicator Selector */}
          <div className="pl-2">
            <h2 className="font-serif text-sm font-semibold text-primary mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" style={{ color: colors.gold }} />
              {language === 'pt' ? 'Selecionar Indicador' : 'Select Indicator'}
            </h2>
            <div className="flex flex-wrap gap-1.5">
              {availableIndicators.map(indicator => (
                <button
                  key={indicator.key}
                  onClick={() => setSelectedIndicator(indicator.key)}
                  className={`
                    flex items-center gap-1 px-2.5 py-1 rounded-md border transition-all text-xs
                    ${selectedIndicator === indicator.key
                      ? 'border-primary bg-primary text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-primary/50'
                    }
                  `}
                  data-testid={`indicator-selector-${indicator.key}`}
                >
                  {selectedIndicator === indicator.key && <Check className="w-3 h-3" />}
                  <span className="font-medium">{indicator.label[language]}</span>
                  <span className={`text-[10px] px-1 py-0.5 rounded ${
                    selectedIndicator === indicator.key ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                    {indicator.unit}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h2 className="font-serif text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" style={{ color: colors.gold }} />
          {language === 'pt' ? 'Evolução Comparativa' : 'Comparative Evolution'}
          <span className="ml-2 text-sm font-normal text-slate-500">
            — {currentIndicator?.label[language]} ({currentIndicator?.unit})
          </span>
        </h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barGap={12} margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="period" 
                tick={{ fill: '#64748B', fontSize: 12 }} 
                interval={0}
                padding={{ left: 30, right: 400 }}
              />
              <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value) => {
                  const ind = currentIndicator;
                  if (ind?.unit === 'cts' || ind?.unit === 't' || ind?.unit === 'm') {
                    return [value.toLocaleString() + ' ' + ind.unit, ind.label[language]];
                  } else if (ind?.unit === '%' || ind?.unit === 'cpht' || ind?.unit === ':1') {
                    return [value.toFixed(2) + ' ' + ind.unit, ind.label[language]];
                  } else {
                    return [value.toLocaleString(), ind?.label[language]];
                  }
                }}
              />
              <Bar 
                dataKey="value" 
                name={currentIndicator?.label[language]} 
                fill={currentIndicator?.color || colors.primary} 
                radius={[4, 4, 0, 0]}
                maxBarSize={80}
              >
                <LabelList 
                  dataKey="value" 
                  position="top" 
                  fill="#374151"
                  fontSize={11}
                  fontWeight={600}
                  formatter={(value) => {
                    const ind = currentIndicator;
                    if (ind?.unit === 'cts' || ind?.unit === 't' || ind?.unit === 'm') {
                      return value.toLocaleString();
                    } else if (ind?.unit === '%' || ind?.unit === 'cpht' || ind?.unit === ':1') {
                      return value.toFixed(2);
                    } else {
                      return value.toLocaleString();
                    }
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* KPI Comparison Table - Compact */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-serif text-sm font-semibold text-primary flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: colors.gold }} />
            {language === 'pt' ? 'Indicadores Comparativos' : 'Comparative Indicators'}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {language === 'pt' 
              ? `Variações calculadas vs ${availablePeriods.find(p => p.id === sortedSelectedPeriods[0])?.label[language]}`
              : `Variances calculated vs ${availablePeriods.find(p => p.id === sortedSelectedPeriods[0])?.label[language]}`}
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left py-2 px-4 text-xs font-semibold text-slate-700 border-b border-slate-200">
                  {language === 'pt' ? 'Indicador' : 'Indicator'}
                </th>
                {sortedSelectedPeriods.map(periodId => {
                  const period = availablePeriods.find(p => p.id === periodId);
                  return (
                    <th key={periodId} className="text-right py-2 px-4 text-xs font-semibold text-slate-700 border-b border-slate-200">
                      {period?.label[language]}
                      {periodId === sortedSelectedPeriods[0] && (
                        <span className="ml-1 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                          {language === 'pt' ? 'Base' : 'Base'}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {kpiDefinitions.map((kpi, idx) => (
                <tr key={kpi.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                  <td className="py-2 px-4 border-b border-slate-100">
                    <span className="text-xs font-medium text-slate-700">{kpi.label[language]}</span>
                    <span className="text-[10px] text-slate-400 ml-1">({kpi.unit})</span>
                  </td>
                  {sortedSelectedPeriods.map(periodId => {
                    const value = periodData[periodId][kpi.key];
                    const variance = getVariance(kpi, periodId);
                    const isBase = periodId === sortedSelectedPeriods[0];
                    
                    let varianceColor = 'text-slate-500';
                    let varianceIcon = <Minus className="w-2.5 h-2.5" />;
                    
                    if (variance !== null) {
                      const isPositiveChange = kpi.inverse ? variance < 0 : variance > 0;
                      varianceColor = isPositiveChange ? 'text-emerald-600' : 'text-red-600';
                      varianceIcon = variance > 0 
                        ? <ArrowUpRight className="w-2.5 h-2.5" /> 
                        : <ArrowDownRight className="w-2.5 h-2.5" />;
                    }
                    
                    return (
                      <td key={periodId} className="py-2 px-4 text-right border-b border-slate-100">
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="font-mono text-sm font-semibold text-primary">
                            {kpi.format(value)}
                          </span>
                          {!isBase && variance !== null && (
                            <span className={`flex items-center gap-0.5 text-[10px] font-medium ${varianceColor}`}>
                              {varianceIcon}
                              {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OperationsMultiPeriodComparative;
